﻿import DefaultApp from './defaultApp';
import { useVuelidate } from '@vuelidate/core';
import { required, requiredIf, email, maxLength, sameAs, helpers } from '@vuelidate/validators';

const isValid = 'Success';
const iscurrency = helpers.regex(/^\d+([.,]\d{2})?$/);
const dutchPostal = helpers.regex(/^[1-9][0-9]{3} ?(?!sa|sd|ss|SA|SD|SS)[A-Za-z]{2}$/);
const isValidDossier = helpers.regex(/^[a-zA-Z0-9]+$/);
function validateIf(condition, rule) {
    return condition ? rule : {};
}

const dateOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/Amsterdam',
};

const directPaymentApp = {
    ...DefaultApp,
    setup: () => ({ v$: useVuelidate() }),
    data() {
        return {
            ...DefaultApp.data(),
            header: null,
            menu: null,
            english: false,
            loading: false,
            step: 1,
            extraStepNeeded: false,
            firstTimeExtra: true,
            formData: {},
            isPR: false,
            isPrefilled: false,
            step1: {
                api: '',
                data: {
                    typeAlias: isValid
                },
                valid: true,
                Dossiernummer: '',
                Postalcode: '',
                IsForeignAddress: false,
                interval: null
            },
            extraStep: {
                DummyDossiernummer: '',
            },
            step2: {
                api: '',
                data: {
                    IdealLink: '',
                    typeAlias: isValid
                },
                valid: true,
                Amount: '',
                ConfirmationEmail: false,
                emailaddress: '',
                succesPaymentReturnUrl: ''
            },
            step3: {
                dateTime: ''
            }
        }
    },
    validations() {
        return {
            step1: {
                Dossiernummer: {
                    required,
                    isValidDossier,
                    maxLength: maxLength(8)
                },
                Postalcode: {
                    required: requiredIf(function () {
                        return !this.step1.IsForeignAddress
                    }),
                    ...validateIf(!this.step1.IsForeignAddress, { dutchPostal })
                },
            },
            extraStep: {
                DummyDossiernummer: {
                    required: requiredIf(function () {
                        return this.step1.IsForeignAddress
                    }),
                    sameAs: sameAs(this.step1.Dossiernummer)
                }
            },
            step2: {
                Amount: {
                    required,
                    iscurrency,
                    minValue: (value) => {
                        return parseFloat(value.replace(/\s/g, "").replace(",", ".")) > 1;
                    }
                },
                emailaddress: {
                    required: requiredIf(function () {
                        return this.step2.ConfirmationEmail
                    }),
                    email
                }
            }
        }
    },
    methods: {
        ...DefaultApp.methods,
        async submit(step) {
            if (step === 1) {
                this.step1.valid = await this.v$.step1.$validate();
                if (!this.step1.valid) {
                    this.scrollTo(document.querySelector('.form-error').closest('.field'));
                } else {
                    if (this.step1.IsForeignAddress) {
                        this.extraStepNeeded = true;
                        if (this.firstTimeExtra) {
                            const validExtra = await this.v$.extraStep.$validate();
                            if (!validExtra) {
                                this.scrollTo(document.querySelector('.form-error').closest('.field'));
                            } else {
                                this.extraStepNeeded = false;
                                this.submitStep1();
                            }
                        } else {
                           this.firstTimeExtra = true;
                        }
                    } else {
                        this.submitStep1();
                    }
                }
            } else if (step === 2) {
                this.step2.valid = await this.v$.step2.$validate();
                if (!this.step2.valid) {
                    this.scrollTo(document.querySelector('.form-error').closest('.field'));
                } else {
                    this.loading = true;
                    this.formData['Amount'] = parseFloat(this.step2.Amount.replace(',','.'));
                    this.formData['ConfirmationEmail'] = this.step2.ConfirmationEmail;
                    this.formData['EmailAddress'] = this.step2.emailaddress;
                    this.formData['SuccessPaymentReturnUrl'] = this.step2.succesPaymentReturnUrl;
                    this.formData['Dossiernummer'] = this.step1.Dossiernummer;
                    let apiPaymentRequest = this.step2.api.replace('{dossiernummer}', this.step1.Dossiernummer);
                    const response = await this.fetchData(this.formData, apiPaymentRequest, 'post');
                    if (response.status === 200) {
                        this.step2.data = response.data;
                        if (this.step2.data.typeAlias === isValid) {
                            this.step = 3;
                        }
                    }
                    this.loading = false;
                }
            }
        },
        async submitStep1() {
            if (this.step1.Dossiernummer.toLowerCase().startsWith("pr")) {
                this.step = 2;
                this.isPR = true;
            } else {
                this.loading = true;
                let apiCheckDossier = this.step1.api.replace('{dossiernummer}', this.step1.Dossiernummer);
                this.formData['PostalCode'] = this.step1.IsForeignAddress ? '' : this.step1.Postalcode;
                this.formData['IsForeignAddress'] = this.step1.IsForeignAddress;
                const response = await this.fetchData(this.formData, apiCheckDossier, 'post');
                if (response.status === 200) {
                    this.step1.data = response.data;
                    if (this.step1.data.typeAlias === isValid || this.step1.data.typeAlias === 'DossiernummerIsTypePR') {
                        this.step = 2;
                    }
                }
                this.loading = false;
            }
        },
        scrollTo($elm) {
            window.scrollTo({ top: $elm.getBoundingClientRect().top + window.pageYOffset - this.header.offsetHeight - this.menu.offsetHeight - 20, behavior: 'smooth' });
        },
        back(step) {
            if (this.step1.IsForeignAddress) {
                this.extraStepNeeded = true;
                this.firstTimeExtra = true
            }
            this.step = step;
        },
        reset() {
            this.isPR = false;
            this.extraStepNeeded = false;
            this.firstTimeExtra = true;
            this.step1.Dossiernummer = '';
            this.step1.Postalcode = '';
            this.step1.IsForeignAddress = false;
            this.v$.$reset();
            this.step = 1;
        },
        checkStep1() {
            if (this.step === 2) {
                if (this.step1.valid) {
                    this.submit(2);
                }
                clearInterval(this.step1.interval);
            }
        }
    },
    async mounted() {
        this.header = document.getElementById('navbar');
        this.menu = document.getElementById('menu');
        if (this.$el.dataset.apistep1) this.step1.api = this.$el.dataset.apistep1;
        if (this.$el.dataset.apistep2) this.step2.api = this.$el.dataset.apistep2;
        if (this.$el.dataset.successurl) this.step2.succesPaymentReturnUrl = this.$el.dataset.successurl;
        if (this.$el.dataset.dossiernumber) this.step1.Dossiernummer = this.$el.dataset.dossiernumber;
        if (this.$el.dataset.postcode) this.step1.Postalcode = this.$el.dataset.postcode;
        if (this.$el.dataset.amount) this.step2.Amount = this.toEuro(this.$el.dataset.amount.replace('.','').replace(',','.')).replace('.','');
        if (this.$el.dataset.email) this.step2.emailaddress = this.$el.dataset.email;
        if (this.$el.dataset.prefilled) this.isPrefilled = this.$el.dataset.prefilled.toLowerCase() === 'true';
        if (this.isPrefilled) {
            if (this.step1.Dossiernummer) {
                if (this.step1.Postalcode) {
                    this.submit(1);
                    if (this.step2.emailaddress) {
                        this.step2.ConfirmationEmail = true;
                        this.step1.interval = setInterval(() => this.checkStep1(), 500);
                    }
                } else {
                    if (this.step2.Amount) {
                        this.step1.IsForeignAddress = true;
                        this.extraStep.DummyDossiernummer = this.step1.Dossiernummer;
                        this.submit(1);
                        if (this.step2.emailaddress) {
                            this.step2.ConfirmationEmail = true;
                            this.step1.interval = setInterval(() => this.checkStep1(), 500);
                        }
                    }
                }
            }
        }
    }
}

export default directPaymentApp;
